import { useEffect, useState } from 'react'
import { api, type Child } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page } from '../components/ui'
import { Empty, ErrorScreen, Loading } from '../components/States'
import { Avatar } from '../components/AvatarPicker'
import { Trace, TraceRegion } from '../lib/trace'
import { ThemePicker } from '../components/ThemePicker'
import { metaFor } from '../themes'
import { Mascot } from '../components/Mascot'
import type { ThemeId } from '../themes'

/**
 * Home. The switcher, and the only place a session begins or ends.
 *
 * A child with an unfinished session gets "Carry on" instead of "Start", which
 * is also what stops two sessions being open for one child at once — there is
 * simply nothing else to press.
 */
export function Home({
  onStart, onAddChild, onSettings, onSignOut, onBadges, onGarden,
  onWordMap, onLevels, onAsk, onPickTheme, onUniverse, onTree, onGames, onGuard, onBuddy,
}: {
  onStart: (child: Child) => void
  /** Left out on a child's tablet, where only a grown-up's device adds a child. */
  onAddChild?: () => void
  onSettings: () => void
  onSignOut: () => void
  onBadges: (child: Child) => void
  onGarden: (child: Child) => void
  onWordMap: (child: Child) => void
  onUniverse: (child: Child) => void
  onTree: (child: Child) => void
  onGames: (child: Child) => void
  onBuddy: (child: Child) => void
  onLevels: (child: Child) => void
  onAsk: (child: Child) => void
  onPickTheme: (child: Child, id: ThemeId) => void
  /** Runs `then` once this child has proved it is them (their PIN). */
  onGuard: (child: Child, then: () => void) => void
}) {
  const [children, setChildren] = useState<Child[] | null>(null)
  const [error, setError] = useState<unknown>(null)

  const load = () => {
    setError(null)
    api.children().then((r) => setChildren(r.children)).catch(setError)
  }
  useEffect(load, [])

  if (error) return <ErrorScreen error={error} onRetry={load} />
  if (!children) return <Loading what="Loading" />

  return (
    <Page width={620}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 24, gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mascot size={30} animate={false} label="" />
          <span style={{ fontSize: 20, fontWeight: 800, color: theme.text }}>Word Sprout</span>
        </div>
        <button
          type="button"
          onClick={onSettings}
          style={{
            fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: theme.muted,
            background: '#FFFFFF', border: `1px solid ${theme.border}`,
            borderRadius: radius.sm, padding: '9px 14px', minHeight: TAP_MIN, cursor: 'pointer',
          }}
        >
          Settings
        </button>
      </header>

      {children.length === 0 ? (
        <Empty
          emoji="👋"
          title="No children yet"
          line="Add your first child and Word Sprout will work out where to start."
          action={onAddChild ? <Button onClick={onAddChild}>Add your first child</Button> : undefined}
        />
      ) : (
        <>
          <Heading>Who is practising?</Heading>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {children.map((child) => (
              <ChildCard
                key={child.learner_id}
                child={child}
                onStart={() => onStart(child)}
                onBadges={() => onBadges(child)}
                onGarden={() => onGarden(child)}
                onWordMap={() => onWordMap(child)}
                onUniverse={() => onUniverse(child)}
                onTree={() => onTree(child)}
                onGames={() => onGames(child)}
                onBuddy={() => onBuddy(child)}
                onLevels={() => onLevels(child)}
                onAsk={() => onAsk(child)}
                onPickTheme={(id) => onPickTheme(child, id)}
                onGuard={(then) => onGuard(child, then)}
              />
            ))}
          </div>
          {onAddChild ? (
            <div style={{ marginTop: 18 }}>
              <Button tone="quiet" onClick={onAddChild}>Add a child</Button>
            </div>
          ) : null}
        </>
      )}

      <div style={{ marginTop: 34, textAlign: 'center' }}>
        <button
          type="button"
          onClick={onSignOut}
          style={{
            fontFamily: 'inherit', fontSize: 14, color: theme.faint,
            background: 'transparent', border: 'none', cursor: 'pointer',
            textDecoration: 'underline', minHeight: TAP_MIN,
          }}
        >
          Sign out
        </button>
      </div>
    </Page>
  )
}

function ChildCard({
  child, onStart, onBadges, onGarden, onWordMap, onLevels, onAsk, onPickTheme, onUniverse,
  onTree, onGames, onGuard, onBuddy,
}: {
  child: Child; onStart: () => void; onBadges: () => void
  onGarden: () => void; onWordMap: () => void; onLevels: () => void
  onUniverse: () => void
  onTree: () => void
  onGames: () => void
  onBuddy: () => void
  onAsk: () => void
  onPickTheme: (id: ThemeId) => void
  onGuard: (then: () => void) => void
}) {
  const [pickingColors, setPickingColors] = useState(false)
  // Held here as well as on the server, so the chosen swatch is marked
  // the instant it is tapped rather than after a reload.
  const [colorChoice, setColorChoice] = useState(child.theme ?? 'sprout')
  const resume = child.resume
  // Clamped. A cursor that had run past the end showed "question 11 of 10"
  // and then asked the server for a question that does not exist.
  const at = resume ? Math.min(resume.cursor + 1, resume.planned_count) : 0
  const left = resume ? Math.max(0, resume.planned_count - resume.cursor) : 0

  const sideLink = (label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: theme.muted,
        background: 'transparent', border: `1px solid ${theme.border}`,
        borderRadius: radius.sm, padding: '6px 10px', cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )

  return (
    <TraceRegion id="T2" note="one row per child">
    <Card style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Trace id="T2.avatar_kind" title="or the uploaded photo on disk">
          <Avatar child={child} size={56} />
        </Trace>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 19, fontWeight: 800, color: theme.text }}>
            <Trace id="T2.display_name">{child.display_name}</Trace>
          </div>
          <div style={{ fontSize: 14, color: theme.faint, marginTop: 2 }}>
            <Trace
              id="calc.rows in T7 learner_word for this child, not counting retired ones"
              title="counted, not stored"
            >
              {child.pool_size ?? 0} words
            </Trace>
            {/* No streak here. It used to say "5 day streak", which the front
                page promises there is none of, and a missed day reset it to
                nothing (D-064). */}
          </div>
          {resume ? (
            <div style={{ fontSize: 14, color: theme.orangeDark, marginTop: 6, fontWeight: 600 }}>
              Stopped at question{' '}
              <Trace id="T12.cursor" title="clamped to planned_count before it is shown">
                {at}
              </Trace>
              {' of '}
              <Trace id="T12.planned_count">{resume.planned_count}</Trace>
              {left > 0 ? (
                <>
                  {' · '}
                  <Trace id="calc.planned_count minus cursor">{left} left</Trace>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* The one thing most visits are for, so the biggest thing on the card,
          with a line saying what comes next (build list item 3, 25 Oct 2026). */}
      <div style={{ marginTop: 14 }}>
        <Button full tone="child" onClick={onStart} style={{ fontSize: 22, padding: '16px 24px' }}>
          {resume ? '▶ Carry on' : '▶ Start practice'}
        </Button>
        <div style={{ fontSize: 14, color: theme.muted, textAlign: 'center', marginTop: 6 }}>
          {resume
            ? (left > 0 ? `Pick up where you stopped: ${left} question${left === 1 ? '' : 's'} to go.` : 'Nearly done: one tap to finish.')
            : 'New words and a few old friends to practise.'}
        </div>
      </div>

      {/* The side rooms, in three named rows (Sam, 8 Oct 2026: "the icons
          seem random; group what belongs together and name each group").
          Always one step from Home, never inside a session. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
        {([
          ['My progress', [
            ['🌲 My tree', onTree], ['🌿 Garden', onGarden],
            ['🏆 Trophies', onBadges], ['⭐ Levels', onLevels],
          ]],
          ['Play and explore', [
            ['🧩 Games', onGames], ['🌌 Universe', onUniverse],
            ['🗺️ Word map', onWordMap], ['✋ Ask for a word', onAsk],
          ]],
          ['Make it mine', [
            ['🌱 My buddy', onBuddy],
            [`${metaFor(colorChoice).emoji} Colors`,
              // A child's colours are their own setting, so their PIN first.
              () => (pickingColors ? setPickingColors(false) : onGuard(() => setPickingColors(true)))],
          ]],
        ] as [string, [string, () => void][]][]).map(([group, links]) => (
          <div key={group}>
            <div style={{
              fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
              color: theme.faint, marginBottom: 5,
            }}>{group}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {links.map(([label, onClick]) => <span key={label}>{sideLink(label, onClick)}</span>)}
            </div>
          </div>
        ))}
      </div>

      {/* Given, never earned: every colour is here from the first minute.
          See themes.ts for why that distinction is the whole point. */}
      {pickingColors ? (
        <div style={{
          marginTop: 14, paddingTop: 14,
          borderTop: `1px solid ${theme.borderSoft}`,
        }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: theme.text2, marginBottom: 10,
          }}>
            Pick your colors
          </div>
          <ThemePicker
            value={colorChoice}
            onPick={(id) => { setColorChoice(id); onPickTheme(id) }}
          />
        </div>
      ) : null}
    </Card>
    </TraceRegion>
  )
}
